import { type entities, utilities } from "@fraction/shared";
import DOMPurify from "dompurify";
import { useMemo } from "react";

interface EmailContent {
  mainContent: string;
  quotedContent: string;
  type: "html" | "text";
}

/**
 * Normalizes newlines in email content:
 * - Single newlines in the middle of text are replaced with spaces (line wrapping artifacts)
 * - Double newlines (paragraph breaks) are preserved
 * - 3+ consecutive newlines are reduced to 2
 */
function normalizeNewlines(text: string): string {
  // First, normalize all line endings to \n
  let normalized = text.replace(/\r\n/g, "\n").replace(/\r/g, "\n");

  // Strip newlines immediately after opening tags and before closing tags
  // These are HTML source formatting, not content
  normalized = normalized.replace(/>\n+/g, ">");
  normalized = normalized.replace(/\n+</g, "<");

  // Trim spaces around newlines
  normalized = normalized.replace(/ +\n/g, "\n").replace(/\n +/g, "\n");

  // Reduce 3+ consecutive newlines to exactly 2 (paragraph breaks)
  normalized = normalized.replace(/\n{3,}/g, "\n\n");

  // Protect double newlines (paragraph breaks) with a marker
  normalized = normalized.replace(/\n\n/g, "<<<PARAGRAPH_BREAK>>>");

  // Replace single newlines with spaces (these are mid-sentence line wrapping artifacts)
  normalized = normalized.replace(/\n/g, " ");

  // Restore paragraph breaks
  normalized = normalized.replace(/<<<PARAGRAPH_BREAK>>>/g, "\n\n");

  // Clean up any multiple spaces that might have been created
  normalized = normalized.replace(/ {2,}/g, " ");

  return normalized.trim();
}

// For plain text, we need to escape HTML entities but preserve newlines
export const escapeHtml = (text: string) => {
  return text
    .replaceAll(/&/g, "&amp;")
    .replaceAll(/</g, "&lt;")
    .replaceAll(/>/g, "&gt;")
    .replaceAll(/"/g, "&quot;")
    .replaceAll(/'/g, "&#039;");
};

export const unescapeHtml = (text: string) => {
  return text
    .replaceAll("&amp;", `&`)
    .replaceAll("&lt;", `<`)
    .replaceAll("&gt;", `>`)
    .replaceAll("&quot;", `"`)
    .replaceAll("&#039;", `'`)
    .replaceAll("&#39;", `'`)
    .replaceAll("&apos;", `'`);
};

/**
 * Cleans up HTML email content:
 * - Removes paragraphs that only contain &nbsp; (Outlook/Word blank lines)
 * - Collapses deeply nested empty div wrappers
 * - Strips MsoNormal inline styles that override font
 */
function cleanHtml(html: string): string {
  let cleaned = html;

  // Remove paragraphs that only contain &nbsp;, whitespace, and/or empty spans
  cleaned = cleaned.replace(/<p[^>]*>(\s*<span[^>]*>\s*(&nbsp;|\u00a0)?\s*<\/span>\s*)*<\/p>/gi, "");

  // Strip inline font-family and font-size styles (let ShadowDOM control fonts)
  cleaned = cleaned.replace(/\s*font-family:[^;"]*;?/gi, "");
  cleaned = cleaned.replace(/\s*font-size:[^;"]*;?/gi, "");

  // Remove empty style attributes left behind
  cleaned = cleaned.replace(/\s*style="\s*"/gi, "");

  // Unwrap known email wrapper divs (gmail, Outlook) — strip the opening tag only,
  // then trim orphaned closing </div> tags from the end
  cleaned = cleaned.replace(
    /<div[^>]*\b(?:dir="ltr"|class="gmail_default"|class="WordSection1")[^>]*>/gi,
    ""
  );
  // Remove trailing </div> tags left behind
  cleaned = cleaned.replace(/(\s*<\/div>\s*)+$/gi, "");

  // Remove <br> tags that appear right before closing </div> tags
  cleaned = cleaned.replace(/<br[^>]*>\s*(<\/div>)/gi, "$1");

  // Remove leading and trailing <br> tags (including <br clear="all">)
  cleaned = cleaned.replace(/^(\s*<br[^>]*>\s*)+/gi, "");
  cleaned = cleaned.replace(/(<br[^>]*>\s*)+$/gi, "");

  // Remove empty divs
  cleaned = cleaned.replace(/<div[^>]*>\s*<\/div>/gi, "");

  // Flatten deeply nested divs (Outlook signature wrappers) - collapse <div><div>...<div> into single <div>
  // Repeatedly unwrap divs that contain only a single div child
  let prev = "";
  while (prev !== cleaned) {
    prev = cleaned;
    cleaned = cleaned.replace(/<div>(\s*<div[\s>])/gi, "$1");
    cleaned = cleaned.replace(/<\/div>(\s*<\/div>)/gi, "$1");
  }

  // Strip leading/trailing whitespace and newlines
  cleaned = cleaned.replace(/^[\s\n\r]+/, "");
  cleaned = cleaned.replace(/[\s\n\r]+$/, "");

  return cleaned;
}

/**
 * Module-level cache of computed email content keyed by a stable identity of
 * the underlying Communication's content. Protects against repeated DOMPurify /
 * regex work when the hook re-mounts (e.g. virtualization, tab switches).
 *
 * Key format: `${comm.id}|${html.length}|${content.length}`.
 *  - `comm.id` is a uuid on the Communication entity, so it can't collide
 *    across comms.
 *  - The length fingerprints guard against stale cache hits if either field is
 *    mutated in place after being cached.
 *
 * Eviction: simple FIFO via Map insertion order — when at capacity, the oldest
 * entry is dropped before inserting.
 */
const EMAIL_CONTENT_CACHE_MAX = 200;
const emailContentCache = new Map<string, EmailContent>();

function computeEmailContent(comm: entities.Communication): EmailContent {
  // Check if we have HTML content
  if (comm.html) {
    const html = utilities.email.splitEmail(comm, { returnHtml: true })?.text || comm.html;

    // For HTML emails, we sanitize the HTML
    let sanitizedHTML = DOMPurify.sanitize(html, {
      ADD_TAGS: ["style"],
      ADD_ATTR: ["target"],
      ALLOW_DATA_ATTR: true,
    });

    // Clean up Outlook/Word HTML artifacts first (unwrap divs, strip empty elements)
    sanitizedHTML = cleanHtml(sanitizedHTML);

    // Normalize newlines in HTML content (in case there are literal newlines)
    sanitizedHTML = normalizeNewlines(sanitizedHTML);

    // Try to split out quoted content from HTML
    // Common patterns in HTML emails for quoted content
    const quotedPatterns = [/<div class="gmail_quote">/i, /<blockquote/i, /<div class=".*quoted.*">/i];

    let mainContent = sanitizedHTML;
    let quotedContent = "";

    for (const pattern of quotedPatterns) {
      const match = sanitizedHTML.match(pattern);
      if (match?.index) {
        mainContent = sanitizedHTML.substring(0, match.index).trim();
        quotedContent = sanitizedHTML.substring(match.index).trim();
        break;
      }
    }

    return {
      mainContent,
      quotedContent,
      type: "html",
    };
  }

  // For plain text emails, use the existing splitEmailText utility
  if (comm.content) {
    const split = utilities.email.splitEmailText(comm.content, { stripOutLinks: true });

    // Normalize newlines before escaping
    const normalizedMainContent = normalizeNewlines(split.text || "");
    const normalizedQuotedContent = normalizeNewlines(split.after || "");

    return {
      mainContent: escapeHtml(normalizedMainContent),
      quotedContent: escapeHtml(normalizedQuotedContent),
      type: "text",
    };
  }

  // Fallback if no content
  return {
    mainContent: "",
    quotedContent: "",
    type: "text",
  };
}

/**
 * Hook to extract and process email content from a Communication entity
 * Separates main content from quoted/replied content
 */
export function useEmailContent(comm: entities.Communication): EmailContent {
  return useMemo(() => {
    // If we don't have a stable id, skip the module cache and just compute —
    // using a non-unique key risks cross-comm collisions.
    if (!comm.id) {
      return computeEmailContent(comm);
    }

    const cacheKey = `${comm.id}|${comm.html?.length ?? 0}|${comm.content?.length ?? 0}`;
    const cached = emailContentCache.get(cacheKey);
    if (cached) {
      return cached;
    }

    const computed = computeEmailContent(comm);

    // Evict oldest entry (FIFO via insertion order) if at capacity.
    if (emailContentCache.size >= EMAIL_CONTENT_CACHE_MAX) {
      const oldestKey = emailContentCache.keys().next().value;
      if (oldestKey !== undefined) {
        emailContentCache.delete(oldestKey);
      }
    }

    emailContentCache.set(cacheKey, computed);
    return computed;
    // `comm` is only used inside computeEmailContent when the cache misses;
    // its content identity is captured by id/html/content, so we intentionally
    // don't include the full `comm` reference in the dep array.
  }, [comm.id, comm.html, comm.content]);
}
